import { UserSearchResult } from "~/types/user";

export const toTitleCase = (str: string): string => {
  return str.replace(
    /\w\S*/g,
    (word) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()
  );
};

export const getFlagImage = (countryName: string) => {
  const countryKey = countryName.toLowerCase().replace(/\s/g, "");
  return `/assets/worldFlags/${countryKey}.png`;
};

// format image url
export const formatImageUrl = (
  url: string,
  resolution: "low" | "medium" | "high" | "original",
  https: boolean = true
) => {
  if (!url || url === "") {
    return "/assets/imgloading.png";
  }
  const baseUrl = "https://waytora.com/cdn-cgi/image";
  // if url doesn't start with http and https is true, add https://
  url = url.startsWith("http") ? url : https ? `https://${url}` : url;

  let width, height, quality;
  if (resolution === "low") {
    width = "width=300";
    height = "height=300";
    quality = "quality=50";
  } else if (resolution === "medium") {
    width = "width=500";
    height = "height=500";
    quality = "quality=75";
  } else if (resolution === "high") {
    width = "width=800";
    height = "height=800";
    quality = "quality=100";
  } else {
    width = "width=1000";
    height = "height=1000";
    quality = "quality=100";
  }

  const format = "format=webp";

  return `${baseUrl}/${width},${height},${quality},${format}/${url}`;
};

// format price
export const formatPrice = (
  currency: string,
  price: number,
  short: boolean = false
) => {
  const symbol = currencyMapping[currency!] || currency;
  // if price is not rounded, round it to nearest whole number based on decimal
  if (!Number.isInteger(price)) {
    price = price % 1 >= 0.5 ? Math.ceil(price) : Math.floor(price);
  }
  // add commas for numbers over 1000
  if (price >= 1000) {
    if (short) {
      return `${symbol}${(price / 1000).toFixed(1).replace(/\.0$/, "")}k`;
    }
    return `${symbol}${price.toLocaleString()}`;
  }
  return `${symbol}${price}`;
};

// currency mapping
export const currencyMapping: Record<string, string> = {
  USD: "$",
  EUR: "€",
  AUD: "A$",
  CAD: "C$",
  JPY: "¥",
  GBP: "£",
};

export const roundUpToWholeNumber = (num: number) => {
  return Math.ceil(num);
};

export const formatReviewScore = (score: number) => {
  return score.toFixed(1);
};

export const formatReviewCount = (count: number) => {
  if (count >= 1000) {
    return (count / 1000).toFixed(2).replace(/\.?0+$/, "") + "k";
  }
  return count.toString();
};

export const getLocationTypeIcon = (type: string): string => {
  const iconMapping: Record<string, string> = {
    "Art Gallery": "fa-palette",
    Beach: "fa-umbrella-beach",
    Restaurant: "fa-utensils",
    Historical: "fa-landmark",
    "Street Market": "fa-store",
    Surfing: "fa-wave",
    Residential: "fa-house",
    "National Park": "fa-tree",
    Parks: "fa-tree",
    Park: "fa-tree",
    Nightlife: "fa-moon",
    Shopping: "fa-shopping-bag",
    "Architectural Wonder": "fa-monument",
    Museums: "fa-museum",
    Museum: "fa-museum",
    Wildlife: "fa-paw",
    "Amusement Park": "fa-ferris-wheel",
    "Food Market": "fa-basket-shopping",
    "Hiking Trail": "fa-person-hiking",
    Clubs: "fa-music",
    Restaurants: "fa-utensils",
    Viewpoint: "fa-mountain",
    Zoo: "fa-hippo",
    Wellness: "fa-spa",
    "Natural Wonder": "fa-mountain",
    Stadium: "fa-futbol",
    Bar: "fa-martini-glass",
    Religious: "fa-place-of-worship",
    Theatre: "fa-masks-theater",
    "viator-experience": "fa-ticket",
  };

  return iconMapping[type] || "fa-location-dot";
};

export const getActivityImage = (images: string[] | null) => {
  if (!images || images.length === 0) {
    return "/assets/imgloading.png";
  }
  return images[0];
};

// format created at ago
export const formatCreatedAtAgo = (createdAt: string) => {
  const now = Date.now();
  const postDate = new Date(createdAt);

  const diffTime = Math.abs(now - postDate.getTime());
  const seconds = Math.floor(diffTime / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years > 0) {
    return `${years} year${years > 1 ? "s" : ""} ago`;
  } else if (months > 0) {
    return `${months} month${months > 1 ? "s" : ""} ago`;
  } else if (days > 0) {
    return `${days} day${days > 1 ? "s" : ""} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  } else {
    return `${seconds} second${seconds > 1 ? "s" : ""} ago`;
  }
};

// flights

export const formatTimeToHHMM = (isoString: string): string => {
  const date = new Date(isoString);

  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");

  // convert to 12 hour format
  const hours12 = date.getUTCHours() % 12 || 12;

  return `${hours12}:${minutes} ${date.getUTCHours() >= 12 ? "PM" : "AM"}`;
};

export const getDominantColor = async (imageUrl: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = imageUrl;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        resolve("rgba(240, 240, 240, 0.3)"); // Default if context is null
        return;
      }

      // Set canvas size to image size
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw image on canvas
      ctx.drawImage(img, 0, 0, img.width, img.height);

      // Get image data
      const imageData = ctx.getImageData(
        0,
        0,
        canvas.width,
        canvas.height
      ).data;

      // Simple algorithm to find dominant color by averaging
      let r = 0,
        g = 0,
        b = 0,
        count = 0;

      // Sample pixels (every 10th pixel to improve performance)
      for (let i = 0; i < imageData.length; i += 40) {
        const red = imageData[i] || 0;
        const green = imageData[i + 1] || 0;
        const blue = imageData[i + 2] || 0;

        r += red;
        g += green;
        b += blue;
        count++;
      }

      // Calculate average
      r = Math.floor(r / count);
      g = Math.floor(g / count);
      b = Math.floor(b / count);

      // Return as rgba with transparency
      resolve(`rgba(${r}, ${g}, ${b}, 0.15)`);
    };

    img.onerror = () => {
      resolve("rgba(240, 240, 240, 0.3)"); // Default light gray on error
    };
  });
};
